import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import api, { API_ENDPOINTS } from '../config/api';
import toast from 'react-hot-toast';
import ProfessionalRichTextEditor from '../components/ProfessionalRichTextEditor';
import { ArrowLeft, Upload, X } from 'lucide-react';

const BLOG_CATEGORIES = [
  'Recruiting',
  'News',
  'Sport',
  'Business',
  'Innovation',
  'Health',
  'Culture',
  'Arts',
  'Travel',
  'Earth',
  'Technology',
  'Education',
  'Entertainment'
];

const CreateBlogPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    category: '',
    thumbnail: '',
    tags: '',
    status: 'draft',
    actionButtonText: '',
    actionButtonLink: ''
  });
  
  const [sections, setSections] = useState([
    { type: 'text', content: '', order: 1 }
  ]);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);

  const createBlogMutation = useMutation(
    async (blogData) => {
      return api.post(API_ENDPOINTS.BLOGS.CREATE, blogData);
    },
    {
      onSuccess: () => {
        toast.success('Blog created successfully!');
        queryClient.invalidateQueries(['myBlogs']);
        navigate('/blogger');
      },
      onError: (error) => {
        const errorData = error.response?.data;
        if (errorData?.errors && Array.isArray(errorData.errors)) {
          errorData.errors.forEach(err => {
            toast.error(err.msg || err.message || 'Validation error');
          });
        } else if (errorData?.error) {
          toast.error(errorData.error);
        } else {
          toast.error('Failed to create blog');
        }
      }
    }
  );

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
        setFormData({ ...formData, thumbnail: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setFormData({ ...formData, thumbnail: '' });
  };

  const addSection = () => {
    setSections([...sections, { type: 'text', content: '', order: sections.length + 1 }]);
  };

  const removeSection = (index) => {
    if (sections.length > 1) {
      const newSections = sections.filter((_, i) => i !== index);
      // Reorder sections
      newSections.forEach((section, i) => {
        section.order = i + 1;
      });
      setSections(newSections);
    }
  };

  const updateSection = (index, field, value) => {
    const newSections = [...sections];
    if (field === 'type') {
      newSections[index] = { ...newSections[index], type: value, content: '' };
    } else {
      newSections[index] = { ...newSections[index], [field]: value };
    }
    setSections(newSections);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.category || !formData.thumbnail) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Process tags
    const tags = formData.tags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    // Process sections - convert RichTextEditor content to HTML string
    const processedSections = sections.map((section, index) => {
      let content = section.content;
      
      // If content is from RichTextEditor (Slate format), convert to HTML
      if (typeof content === 'object' && content !== null && !Array.isArray(content)) {
        // RichTextEditor returns HTML string, so content should already be a string
        content = content;
      }
      
      return {
        type: section.type,
        content: content,
        order: index + 1
      };
    });

    const blogData = {
      title: formData.title,
      excerpt: formData.excerpt,
      category: formData.category,
      thumbnail: formData.thumbnail,
      tags: tags,
      sections: processedSections,
      status: formData.status,
      actionButton: {
        text: formData.actionButtonText || undefined,
        link: formData.actionButtonLink || undefined
      }
    };

    createBlogMutation.mutate(blogData);
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/blogger')}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-accent mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Blogger Dashboard
        </button>

        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
          Create New Blog
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              maxLength={200}
              className="w-full input-field"
              placeholder="Enter blog title"
            />
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Excerpt
            </label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              maxLength={300}
              rows={3}
              className="w-full input-field"
              placeholder="Short description of your blog"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
              className="w-full input-field"
            >
              <option value="">Select a category</option>
              {BLOG_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Thumbnail */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Thumbnail Image *
            </label>
            {thumbnailPreview ? (
              <div className="relative inline-block">
                <img
                  src={thumbnailPreview}
                  alt="Thumbnail preview"
                  className="w-64 h-48 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={removeThumbnail}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <label className="cursor-pointer">
                  <span className="text-accent hover:text-accent/80">Click to upload</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleThumbnailChange}
                    className="hidden"
                  />
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Or paste image URL below
                </p>
              </div>
            )}
            <input
              type="text"
              value={formData.thumbnail}
              onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
              placeholder="Or enter image URL"
              className="w-full input-field mt-2"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full input-field"
              placeholder="tag1, tag2, tag3"
            />
          </div>

          {/* Sections */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
              Content Sections
            </label>
            {sections.map((section, index) => (
              <div key={index} className="mb-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <select
                    value={section.type}
                    onChange={(e) => updateSection(index, 'type', e.target.value)}
                    className="input-field"
                  >
                    <option value="text">Text</option>
                    <option value="heading">Heading</option>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="quote">Quote</option>
                    <option value="code">Code</option>
                    <option value="table">Table</option>
                    <option value="list">List</option>
                  </select>
                  {sections.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSection(index)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
                {section.type === 'text' || section.type === 'heading' || section.type === 'quote' || section.type === 'table' || section.type === 'list' ? (
                  <ProfessionalRichTextEditor
                    value={section.content || ''}
                    onChange={(html) => updateSection(index, 'content', html)}
                    placeholder={section.type === 'heading' ? 'Enter heading (use H1, H2, H3 buttons in toolbar)...' : section.type === 'table' ? 'Use the table button in the toolbar to insert a table...' : 'Enter content...'}
                  />
                ) : section.type === 'image' ? (
                  <div>
                    <input
                      type="text"
                      value={section.content?.url || ''}
                      onChange={(e) => updateSection(index, 'content', { url: e.target.value, alt: section.content?.alt || '', caption: section.content?.caption || '' })}
                      placeholder="Image URL"
                      className="w-full input-field mb-2"
                    />
                    <input
                      type="text"
                      value={section.content?.alt || ''}
                      onChange={(e) => updateSection(index, 'content', { ...section.content, alt: e.target.value })}
                      placeholder="Alt text"
                      className="w-full input-field mb-2"
                    />
                    <input
                      type="text"
                      value={section.content?.caption || ''}
                      onChange={(e) => updateSection(index, 'content', { ...section.content, caption: e.target.value })}
                      placeholder="Caption (optional)"
                      className="w-full input-field"
                    />
                  </div>
                ) : section.type === 'video' ? (
                  <div>
                    <input
                      type="text"
                      value={section.content?.url || ''}
                      onChange={(e) => updateSection(index, 'content', { url: e.target.value, platform: section.content?.platform || 'youtube' })}
                      placeholder="Video URL"
                      className="w-full input-field mb-2"
                    />
                    <select
                      value={section.content?.platform || 'youtube'}
                      onChange={(e) => updateSection(index, 'content', { ...section.content, platform: e.target.value })}
                      className="w-full input-field"
                    >
                      <option value="youtube">YouTube</option>
                      <option value="vimeo">Vimeo</option>
                      <option value="direct">Direct URL</option>
                    </select>
                  </div>
                ) : section.type === 'code' ? (
                  <div>
                    <textarea
                      value={section.content?.code || ''}
                      onChange={(e) => updateSection(index, 'content', { code: e.target.value, language: section.content?.language || 'javascript' })}
                      placeholder="Code"
                      rows={10}
                      className="w-full input-field font-mono mb-2"
                    />
                    <input
                      type="text"
                      value={section.content?.language || 'javascript'}
                      onChange={(e) => updateSection(index, 'content', { ...section.content, language: e.target.value })}
                      placeholder="Language"
                      className="w-full input-field"
                    />
                  </div>
                ) : null}
              </div>
            ))}
            <button
              type="button"
              onClick={addSection}
              className="btn-secondary text-sm w-full mt-4"
            >
              + Add Section
            </button>
          </div>

          {/* Action Button */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Action Button
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Add a call-to-action button that will be displayed on the blog detail page. This button will open in a new tab when clicked.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Button Text
                </label>
                <input
                  type="text"
                  value={formData.actionButtonText}
                  onChange={(e) => setFormData({ ...formData, actionButtonText: e.target.value })}
                  className="w-full input-field"
                  placeholder="e.g., Learn More, Get Started, Download Now"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Button Link
                </label>
                <input
                  type="url"
                  value={formData.actionButtonLink}
                  onChange={(e) => setFormData({ ...formData, actionButtonLink: e.target.value })}
                  className="w-full input-field"
                  placeholder="https://example.com"
                />
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full input-field"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>

          {/* Submit Buttons */}
          <div className="flex items-center justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/blogger')}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createBlogMutation.isLoading}
              className="btn-primary disabled:opacity-50"
            >
              {createBlogMutation.isLoading ? 'Creating...' : 'Create Blog'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBlogPage;
